import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { formatEventDateByPref } from '../utils/formatting';

// (31 Oct 2026, decision 163) "Simulate poll dates": the administrator types an
// event date and sees, step by step, when this calendar's poll would open, remind
// and close, worked out from the values in the boxes above (saved or not). Display
// only: nothing is saved or sent. Dates are YYYY-MM-DD strings shifted in UTC, so no
// time zone can move a day; shown in the user's own Date Format.
const shiftIsoDate = (iso, days) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};
const localTodayIso = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const daysBeforeLabel = (n) => (n === 1 ? '1 day before' : `${n} days before`);
// (31 Oct 2026) The late-start warning matches pollScheduler.js's MIN_OPEN_DAYS (3) and its
// cap at the event date; keep the two in step.
// (31 Oct 2026) Reminders also say how they relate to their own setting (Neil's option 3):
// "15 days before (1 day before Phase 1 closes)".

const PollDateSimulator = ({ openMode, p1OpenDays, p2OpenDays, p2CloseDays, r1Days, r2Days, timingProblem, dateFormat, showWeekday }) => {
  const [eventDate, setEventDate] = useState('');
  const fmt = (iso) => formatEventDateByPref(iso, dateFormat, showWeekday);
  const today = localTodayIso();

  if (timingProblem) {
    return <p className="text-xs text-red-600">Correct the day settings above first: {timingProblem}</p>;
  }

  const p1Open = Number(p1OpenDays);
  const p2Open = Number(p2OpenDays);
  const p2Close = Number(p2CloseDays);
  const r1 = r1Days === '' || r1Days === null || Number(r1Days) === 0 ? null : Number(r1Days);
  const r2 = r2Days === '' || r2Days === null || Number(r2Days) === 0 ? null : Number(r2Days);

  // kind: 'end' (the event), 'step' (a phase opening or closing), 'reminder'.
  const steps = [];
  const warnings = [];
  if (eventDate) {
    if (openMode === 'days_before') {
      steps.push({ kind: 'end', title: 'Enter the event any time before', iso: shiftIsoDate(eventDate, -p1Open), note: 'Members are sent a WhatsApp as usual and calendars are updated', when: 'Beforehand' });
      steps.push({ kind: 'step', title: 'Poll Phase 1 opens', iso: shiftIsoDate(eventDate, -p1Open), note: 'WhatsApp to the group; members are emailed', when: daysBeforeLabel(p1Open) });
    } else if (openMode === 'on_creation') {
      steps.push({ kind: 'end', title: 'You enter the event', iso: null, note: 'Members are sent a WhatsApp as usual and calendars are updated', when: 'Any time beforehand' });
      steps.push({ kind: 'step', title: 'Poll Phase 1 opens the morning after you enter the event', iso: null, note: 'WhatsApp to the group; members are emailed', when: 'The next morning' });
    } else {
      steps.push({ kind: 'end', title: 'You enter the event', iso: null, note: 'Members are sent a WhatsApp as usual and calendars are updated', when: 'Any time beforehand' });
      steps.push({ kind: 'step', title: 'Poll Phase 1 opens when you press Open Phase 1 (offers) on the event', iso: null, note: 'WhatsApp to the group; members are emailed', when: 'When you choose' });
    }
    if (r1 !== null) steps.push({ kind: 'reminder', title: 'Phase 1 reminder sent', iso: shiftIsoDate(eventDate, -(p2Open + r1)), note: 'WhatsApp to the group; members are emailed', when: `${daysBeforeLabel(p2Open + r1)} (${r1 === 1 ? '1 day' : `${r1} days`} before Phase 1 closes)` });
    steps.push({ kind: 'step', title: 'Phase 1 closes and Phase 2 opens', iso: shiftIsoDate(eventDate, -p2Open), note: 'WhatsApp to the group; members are emailed', when: daysBeforeLabel(p2Open) });
    if (r2 !== null) steps.push({ kind: 'reminder', title: 'Phase 2 reminder sent', iso: shiftIsoDate(eventDate, -(p2Close + r2)), note: 'WhatsApp to the group; members are emailed', when: `${daysBeforeLabel(p2Close + r2)} (${r2 === 1 ? '1 day' : `${r2} days`} before Phase 2 closes)` });
    steps.push({ kind: 'step', title: 'Phase 2 closes', iso: shiftIsoDate(eventDate, -p2Close), note: 'WhatsApp to the group; members are emailed', when: daysBeforeLabel(p2Close) });
    steps.push({ kind: 'end', title: 'The event takes place', iso: eventDate, note: null, when: 'The event' });

    // Warnings, in plain words.
    if (eventDate <= today) {
      warnings.push('This event date is today or has already passed.');
    } else if (steps.some((s) => s.iso && s.kind !== 'end' && s.iso <= today)) {
      warnings.push("Some of these dates are today or have already passed (shown in red). For an event on this date entered now, the poll would start late, so these dates would not apply: each phase would stay open at least 3 days from when it opens, but never past the event date.");
    }
    if (r1 !== null && openMode === 'days_before' && p2Open + r1 >= p1Open) {
      warnings.push('The Phase 1 reminder comes on or before the day Phase 1 opens.');
    }
    if (r2 !== null && p2Close + r2 >= p2Open) {
      warnings.push('The Phase 2 reminder comes on or before the day Phase 2 opens.');
    }
    // Only while there is still time to act on it; once that day has gone, the
    // "already passed" warning above says what matters.
    const lastUseful = shiftIsoDate(eventDate, -(p2Open + 2));
    if (openMode === 'on_creation' && eventDate > today && lastUseful >= today) {
      warnings.push(`Phase 1 lasts from the morning after you enter the event until ${fmt(shiftIsoDate(eventDate, -p2Open))}. To give members at least a day to offer, enter this event by ${fmt(lastUseful)}.`);
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <label className="block text-sm font-medium text-gray-800 mb-1">Event date</label>
      <input
        type="date"
        value={eventDate}
        onChange={(e) => setEventDate(e.target.value)}
        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
      />
      <p className="text-xs text-gray-500 mt-1">
        Uses the values above, saved or not. Nothing is saved or sent. The poll's steps happen in the early morning.
      </p>

      {eventDate && (
        <div className="mt-3 space-y-1">
          {steps.map((s, i) => {
            const past = s.iso && s.kind !== 'end' && s.iso <= today;
            const box = s.kind === 'end'
              ? 'bg-rose-900 text-white rounded-full'
              : s.kind === 'reminder'
                ? 'bg-white border border-dashed border-gray-400 rounded-md'
                : 'bg-white border-2 border-rose-900 rounded-md';
            const dateText = s.iso ? fmt(s.iso) : '';
            const head = s.kind === 'end' || !dateText
              ? (dateText ? `${s.title} ${dateText}` : s.title)
              : `${s.title} on ${dateText}, early morning`;
            return (
              <React.Fragment key={i}>
                {i > 0 && <div className="text-center text-gray-400 text-xs leading-none" aria-hidden="true">↓</div>}
                <div className="flex items-center gap-3">
                  <div className={`flex-1 px-3 py-2 text-center ${box} ${past ? 'ring-2 ring-red-500' : ''}`}>
                    <div className={`text-sm ${s.kind === 'reminder' ? '' : 'font-semibold'} ${past ? 'text-red-700' : ''}`}>{head}</div>
                    {s.note && <div className={`text-xs italic ${s.kind === 'end' ? 'text-rose-100' : 'text-rose-900'}`}>{s.note}</div>}
                  </div>
                  <div className="w-28 shrink-0 text-xs text-gray-500">{s.when}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {warnings.length > 0 && (
        <ol className="mt-3 list-decimal pl-5 space-y-1 text-xs text-amber-800">
          {warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ol>
      )}
    </div>
  );
};

// (10 Sep 2026) New tab — GA self-service poll setup. Before this, NO UI
// existed anywhere for authoring/amending a poll template (course names)
// or toggling poll_pass1_auto_open/poll_pass2_trigger_days/
// poll_pass2_close_trigger_days — every real template and setting in the
// system was inserted via direct SQL during testing. Deliberately a
// short, fixed-shape form (4 course-name fields + one toggle + two day
// counts) rather than a general question editor, to avoid exactly the
// "filling in a form" friction this whole feature is meant to prevent
// downstream for members. The "Attending?" question itself is fixed/
// system-generated server-side and not editable here at all.
//
// Self-contained tab, same pattern as MembersTab.jsx/CalendarTab.jsx —
// fetches its own data on mount and saves via its own button, not the
// shared modal footer's "Save Changes" (see SettingsModal.jsx's footer
// exclusion list).
// (14 Sep 2026) Blank input, null, and 0 are all "reminders disabled" for
// a phase — normalized to a single canonical value (null) so baseline
// comparisons and the PATCH body don't have to special-case three
// different representations of the same thing.
const normalizeReminderDays = (v) => (v === '' || v === null || v === undefined || Number(v) === 0 ? null : Number(v));

// (15 Sep 2026) Moved the Save button out of this tab and into
// SettingsModal.jsx's shared footer, per Neil's request — the tab's own
// scroll area could push it out of view, especially once the two
// reminder-day fields were added 14 Sep. Save/hasChanges/isSaving logic
// stays entirely local to this component (self-contained data flow
// unchanged); only the button's PRESENTATION moves up, via forwardRef
// (exposes save()) and onStateChange (reports hasChanges/isSaving so the
// footer button can reflect and trigger it).
const PollSettingsTab = forwardRef(({ currentCalendar, apiBaseUrl, authToken, onStatusMessage, onStateChange, dateFormat, showWeekday }, ref) => {
  const [isLoading, setIsLoading] = useState(true);
  // (31 Oct 2026, decision 163) The simulator is shown only after its button is pressed.
  const [showSimulator, setShowSimulator] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [templateExists, setTemplateExists] = useState(false);
  // (26 Sep 2026) Replaces the old on/off pollPass1AutoOpen checkbox:
  // 'on_creation' | 'days_before' | 'manual' (calendars.poll_pass1_open_mode),
  // plus the day count for 'days_before' (poll_pass1_trigger_days).
  const [pollPass1OpenMode, setPollPass1OpenMode] = useState('on_creation');
  const [pollPass1TriggerDays, setPollPass1TriggerDays] = useState(14);
  const [pollPass2TriggerDays, setPollPass2TriggerDays] = useState(14);
  const [pollPass2CloseTriggerDays, setPollPass2CloseTriggerDays] = useState(7);
  // (14 Sep 2026) null means "reminders disabled for this phase" — a
  // genuinely different state from 0 in principle, but both are treated
  // identically by the scheduler (see normalizeReminderDays below), so
  // there's no meaningful distinction to preserve here either.
  const [pollPass1ReminderDays, setPollPass1ReminderDays] = useState(null);
  const [pollPass2ReminderDays, setPollPass2ReminderDays] = useState(null);
  const [courses, setCourses] = useState(['', '', '', '']);
  // (11 Sep 2026) Baseline snapshot of the last-loaded/last-saved values.
  // Added for two real bugs found in live testing: (1) handleSave was
  // unconditionally sending poll_pass2_trigger_days/
  // poll_pass2_close_trigger_days on every save, even when untouched —
  // which meant toggling ONLY pollPass1AutoOpen still tripped the
  // backend's open-poll gate on the day-count fields, even though that
  // gate deliberately excludes the boolean (see calendarRoutes.js). (2)
  // the Save button had no way to know whether anything had actually
  // changed — its only disabled condition was isSaving — so it stayed
  // enabled even when the visible values matched what was last saved.
  // Comparing current state against this baseline fixes both: only
  // fields that actually differ are sent, and the button disables
  // whenever nothing differs.
  const [baseline, setBaseline] = useState(null);

  useEffect(() => {
    if (!currentCalendar) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/poll-settings`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        const data = await res.json();
        if (res.ok) {
          const loadedCourses = data.courses && data.courses.length === 4 ? data.courses : ['', '', '', ''];
          const loadedPollPass1OpenMode = data.pollPass1OpenMode || (data.pollPass1AutoOpen ? 'on_creation' : 'manual');
          const loadedPollPass1TriggerDays = data.pollPass1TriggerDays ?? 14;
          const loadedPollPass2TriggerDays = data.pollPass2TriggerDays ?? 14;
          const loadedPollPass2CloseTriggerDays = data.pollPass2CloseTriggerDays ?? 7;
          const loadedPollPass1ReminderDays = data.pollPass1ReminderDays ?? null;
          const loadedPollPass2ReminderDays = data.pollPass2ReminderDays ?? null;
          setPollPass1OpenMode(loadedPollPass1OpenMode);
          setPollPass1TriggerDays(loadedPollPass1TriggerDays);
          setPollPass2TriggerDays(loadedPollPass2TriggerDays);
          setPollPass2CloseTriggerDays(loadedPollPass2CloseTriggerDays);
          setPollPass1ReminderDays(loadedPollPass1ReminderDays);
          setPollPass2ReminderDays(loadedPollPass2ReminderDays);
          setCourses(loadedCourses);
          setTemplateExists(!!data.templateExists);
          setBaseline({
            pollPass1OpenMode: loadedPollPass1OpenMode,
            pollPass1TriggerDays: loadedPollPass1TriggerDays,
            pollPass2TriggerDays: loadedPollPass2TriggerDays,
            pollPass2CloseTriggerDays: loadedPollPass2CloseTriggerDays,
            pollPass1ReminderDays: loadedPollPass1ReminderDays,
            pollPass2ReminderDays: loadedPollPass2ReminderDays,
            courses: loadedCourses,
          });
        } else {
          onStatusMessage && onStatusMessage(data.error || 'Failed to load poll settings', 'error');
        }
      } catch (err) {
        onStatusMessage && onStatusMessage('Failed to load poll settings', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [currentCalendar, apiBaseUrl, authToken, onStatusMessage]);

  const handleCourseChange = (index, value) => {
    setCourses((prev) => prev.map((c, i) => (i === index ? value : c)));
  };

  // Whether anything differs from the last-loaded/last-saved baseline —
  // OR no template exists yet at all. The latter is needed because the
  // suggested onboarding defaults ARE the baseline on first load, so
  // accepting them as-is (a genuinely valid action, per the banner's own
  // "Save to create it") would otherwise never count as a change and the
  // button would stay disabled forever unless a value was edited first.
  // false while baseline hasn't loaded yet, so the button can't be
  // enabled before initial load completes.
  const hasChanges = !!baseline && (
    !templateExists ||
    pollPass1OpenMode !== baseline.pollPass1OpenMode ||
    Number(pollPass1TriggerDays) !== Number(baseline.pollPass1TriggerDays) ||
    Number(pollPass2TriggerDays) !== Number(baseline.pollPass2TriggerDays) ||
    Number(pollPass2CloseTriggerDays) !== Number(baseline.pollPass2CloseTriggerDays) ||
    normalizeReminderDays(pollPass1ReminderDays) !== normalizeReminderDays(baseline.pollPass1ReminderDays) ||
    normalizeReminderDays(pollPass2ReminderDays) !== normalizeReminderDays(baseline.pollPass2ReminderDays) ||
    courses.some((c, i) => c !== baseline.courses[i])
  );

  useEffect(() => {
    onStateChange && onStateChange({ hasChanges, isSaving });
  }, [hasChanges, isSaving, onStateChange]);

  // (26 Sep 2026) The day counts must nest, counted in days before the event:
  // Phase 1 opens (only for 'days_before') > Phase 1 closes / Phase 2 opens >
  // Phase 2 closes. The server checks this too; checking here gives the
  // message before anything is sent.
  const timingProblem = (() => {
    const open = Number(pollPass1TriggerDays);
    const p2Open = Number(pollPass2TriggerDays);
    const p2Close = Number(pollPass2CloseTriggerDays);
    if (!(p2Open > p2Close)) return `Phase 1 must close more days before the event than Phase 2 closes (now ${p2Open} and ${p2Close}).`;
    if (pollPass1OpenMode === 'days_before' && !(open > p2Open)) return `Phase 1 must open more days before the event than it closes (now ${open} and ${p2Open}).`;
    return null;
  })();

  // (26 Sep 2026) Choosing "A set number of days before" on a calendar whose
  // numbers don't allow it (e.g. open 14, close 14) fills in the suggested
  // 14 / 7 / 3 — the GA can still change any of them before saving.
  const chooseOpenMode = (mode) => {
    setPollPass1OpenMode(mode);
    if (mode === 'days_before') {
      const open = Number(pollPass1TriggerDays);
      const p2Open = Number(pollPass2TriggerDays);
      const p2Close = Number(pollPass2CloseTriggerDays);
      if (!(open > p2Open && p2Open > p2Close)) {
        setPollPass1TriggerDays(14);
        setPollPass2TriggerDays(7);
        setPollPass2CloseTriggerDays(3);
      }
    }
  };

  const handleSave = async () => {
    if (!baseline) return;
    if (timingProblem) {
      onStatusMessage && onStatusMessage(timingProblem, 'error');
      return;
    }
    setIsSaving(true);
    try {
      // Only include fields that actually changed from baseline — this is
      // the fix for bug (1) above: previously the two day-count fields
      // were always sent, which tripped the backend's open-poll gate on
      // any save, even a pure boolean toggle the gate is meant to allow.
      const patchBody = {};
      if (pollPass1OpenMode !== baseline.pollPass1OpenMode) {
        patchBody.poll_pass1_open_mode = pollPass1OpenMode;
      }
      if (Number(pollPass1TriggerDays) !== Number(baseline.pollPass1TriggerDays)) {
        patchBody.poll_pass1_trigger_days = Number(pollPass1TriggerDays);
      }
      if (Number(pollPass2TriggerDays) !== Number(baseline.pollPass2TriggerDays)) {
        patchBody.poll_pass2_trigger_days = Number(pollPass2TriggerDays);
      }
      if (Number(pollPass2CloseTriggerDays) !== Number(baseline.pollPass2CloseTriggerDays)) {
        patchBody.poll_pass2_close_trigger_days = Number(pollPass2CloseTriggerDays);
      }
      if (normalizeReminderDays(pollPass1ReminderDays) !== normalizeReminderDays(baseline.pollPass1ReminderDays)) {
        patchBody.poll_pass1_reminder_days = normalizeReminderDays(pollPass1ReminderDays);
      }
      if (normalizeReminderDays(pollPass2ReminderDays) !== normalizeReminderDays(baseline.pollPass2ReminderDays)) {
        patchBody.poll_pass2_reminder_days = normalizeReminderDays(pollPass2ReminderDays);
      }

      if (Object.keys(patchBody).length > 0) {
        const patchRes = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(patchBody),
        });
        const patchData = await patchRes.json();
        if (!patchRes.ok) {
          onStatusMessage && onStatusMessage(patchData.error || 'Failed to save poll timing settings', 'error');
          return;
        }
      }

      // (12 Sep 2026) Was `courses.some(...)` alone — meant a save that
      // only touched pollPass1AutoOpen or a day-count field, with the
      // suggested course defaults left untouched, would PATCH calendars
      // but never call POST /poll-template at all. templateExists stayed
      // false forever, and "Open Phase 1" kept failing with "no poll
      // template set up" even after a successful-looking save. Creating
      // the template is required whenever one doesn't exist yet,
      // independent of whether the course names themselves changed.
      const coursesChanged = !templateExists || courses.some((c, i) => c !== baseline.courses[i]);
      if (coursesChanged) {
        const templateRes = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/poll-template`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ courses }),
        });
        const templateData = await templateRes.json();
        if (!templateRes.ok) {
          onStatusMessage && onStatusMessage(templateData.error || 'Failed to save poll courses', 'error');
          return;
        }
        setTemplateExists(true);
      }

      // Advance the baseline to what was just successfully saved, so
      // hasChanges (and therefore the Save button) correctly goes back
      // to false/disabled — this is the fix for bug (2) above.
      setBaseline({
        pollPass1OpenMode,
        pollPass1TriggerDays: Number(pollPass1TriggerDays),
        pollPass2TriggerDays: Number(pollPass2TriggerDays),
        pollPass2CloseTriggerDays: Number(pollPass2CloseTriggerDays),
        pollPass1ReminderDays: normalizeReminderDays(pollPass1ReminderDays),
        pollPass2ReminderDays: normalizeReminderDays(pollPass2ReminderDays),
        courses,
      });
      onStatusMessage && onStatusMessage('Poll settings saved.', 'success');
    } catch (err) {
      onStatusMessage && onStatusMessage('Failed to save poll settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save: handleSave }));

  if (isLoading) {
    return <p className="text-sm text-gray-500 p-1">Loading poll settings…</p>;
  }

  return (
    <div className="space-y-5">
      {!templateExists && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          No poll has ever been set up for this calendar yet — these are suggested starting values. Save to create it.
        </p>
      )}

      {/* (26 Sep 2026) Three options in place of the old on/off checkbox. */}
      <div>
        <p className="text-sm font-medium text-gray-800 mb-2">When should Phase 1 (offers) open?</p>
        <div className="space-y-2">
          {[
            { value: 'on_creation', label: 'As soon as the event is created', detail: 'The poll opens the morning after you create the event.' },
            { value: 'days_before', label: 'A set number of days before the event', detail: null },
            { value: 'manual', label: 'Only when I open it', detail: 'Nothing opens by itself: use Open Phase 1 (offers) on the event.' },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                pollPass1OpenMode === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="poll-pass1-open-mode"
                value={opt.value}
                checked={pollPass1OpenMode === opt.value}
                onChange={() => chooseOpenMode(opt.value)}
                className="mt-1"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-gray-800">{opt.label}</span>
                {opt.detail && <span className="block text-xs text-gray-600">{opt.detail}</span>}
                {opt.value === 'days_before' && pollPass1OpenMode === 'days_before' && (
                  <span className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                    Opens
                    <input
                      type="number"
                      min="0"
                      value={pollPass1TriggerDays}
                      onChange={(e) => setPollPass1TriggerDays(e.target.value)}
                      className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    />
                    days before the event
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Whichever you choose, you can always open Phase 1 early with the button on the event.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-800 mb-1">
          Days before the event that Phase 1 closes / Phase 2 opens
        </label>
        <input
          type="number"
          min="0"
          value={pollPass2TriggerDays}
          onChange={(e) => setPollPass2TriggerDays(e.target.value)}
          className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-800 mb-1">
          Send a reminder this many days before Phase 1 closes
        </label>
        <input
          type="number"
          min="0"
          value={pollPass1ReminderDays === null ? '' : pollPass1ReminderDays}
          onChange={(e) => setPollPass1ReminderDays(e.target.value)}
          placeholder="No reminder"
          className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave blank for no reminder. Emailed to members according to their own email settings, whether or not they've already responded. You'll also be sent the WhatsApp message to post to the group.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-800 mb-1">
          Days before the event that Phase 2 closes
        </label>
        <input
          type="number"
          min="0"
          value={pollPass2CloseTriggerDays}
          onChange={(e) => setPollPass2CloseTriggerDays(e.target.value)}
          className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          The day settings can't be changed while a poll is currently open for an upcoming event.
        </p>
        {timingProblem && (
          <p className="text-xs text-red-600 mt-1">{timingProblem}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-800 mb-1">
          Send a reminder this many days before Phase 2 closes
        </label>
        <input
          type="number"
          min="0"
          value={pollPass2ReminderDays === null ? '' : pollPass2ReminderDays}
          onChange={(e) => setPollPass2ReminderDays(e.target.value)}
          placeholder="No reminder"
          className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave blank for no reminder. Emailed to members according to their own email settings, whether or not they've already responded. You'll also be sent the WhatsApp message to post to the group.
        </p>
      </div>

      {/* (31 Oct 2026, decision 163) Before Courses, as Neil asked. */}
      <div>
        <button
          type="button"
          onClick={() => setShowSimulator((v) => !v)}
          className="px-3 py-1.5 text-sm rounded-lg border border-blue-600 text-blue-700 hover:bg-blue-50"
        >
          {showSimulator ? 'Hide the simulation' : 'Simulate poll dates'}
        </button>
        <p className="text-xs text-gray-500 mt-1">
          Enter an event date to see when its poll would open, send reminders and close with these settings.
        </p>
        {showSimulator && (
          <div className="mt-3">
            <PollDateSimulator
              openMode={pollPass1OpenMode}
              p1OpenDays={pollPass1TriggerDays}
              p2OpenDays={pollPass2TriggerDays}
              p2CloseDays={pollPass2CloseTriggerDays}
              r1Days={pollPass1ReminderDays}
              r2Days={pollPass2ReminderDays}
              timingProblem={timingProblem}
              dateFormat={dateFormat}
              showWeekday={showWeekday}
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-800 mb-2">Courses</label>
        <p className="text-xs text-gray-500 mb-2">
          Up to 4 courses members can offer a dish for. Leave a field blank to not use that slot.
        </p>
        <div className="space-y-2">
          {courses.map((course, i) => (
            <input
              key={i}
              type="text"
              value={course}
              onChange={(e) => handleCourseChange(i, e.target.value)}
              placeholder={`Course ${i + 1} (e.g. Starter)`}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            />
          ))}
        </div>
      </div>
    </div>
  );
});

export default PollSettingsTab;
